import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import { buildCharacter, type CharacterRig } from '@/meshkit/character/build';
import { CharacterAnimator } from '@/meshkit/character/animator';
import { LOYAL_SOLDIER } from '@/meshkit/character/spec';
import type { CollisionWorld } from '@/world/collision';
import { bodies, moveActor } from '@/world/bodies';
import type { Faction } from '@/gameplay/GameState';
import { Weapon, type Combat, type CombatTarget, type WeaponSpec } from '@/gameplay/Combat';
import type { Enemy } from '@/gameplay/Enemy';

export type FollowerState = 'waiting' | 'following' | 'cover' | 'shoot' | 'rescued' | 'down';

export interface FollowerConfig {
  readonly id: number;
  readonly spawn: THREE.Vector2;
  readonly slot: number;
  readonly pickupRadius?: number;
  readonly rescueRadius?: number;
  readonly followDistance?: number;
  readonly spacing?: number;
  readonly speed?: number;
  readonly coverSpeed?: number;
  readonly coverHoldTime?: number;
  readonly maxHealth?: number;
  readonly weapon?: WeaponSpec;
  readonly rig?: CharacterRig;
}

export interface FollowerSnapshot {
  readonly id: number;
  readonly state: FollowerState;
  readonly health: number;
  readonly position: THREE.Vector2;
  readonly slot: number;
}

export interface FollowerUpdateContext {
  readonly leader: CombatTarget;
  readonly followers: readonly Follower[];
  readonly safehouse: THREE.Vector2;
  readonly world: CollisionWorld;
  readonly enemies?: readonly Enemy[];
  readonly combat?: Combat;
}

const FOLLOWER_DEFAULTS = {
  pickupRadius: 2.4,
  rescueRadius: 1.25,
  followDistance: 1.45,
  spacing: 0.82,
  speed: 3.0,
  coverSpeed: 3.4,
  coverHoldTime: 1.15,
  leashDistance: 6.2,
  shootRange: 10,
  engageRange: 13,
  maxHealth: 70,
  radius: 0.32,
  turnRate: 11,
} as const;

const ARRIVE_EPSILON = 0.16;
const ACTOR_GAP = 0.08;
const FOLLOWER_PISTOL: WeaponSpec = {
  name: 'Snubnose',
  damage: 15,
  range: 22,
  magazine: 6,
  fireInterval: 0.58,
  reloadTime: 2.0,
  spread: 0.055,
  automatic: false,
};

export class Follower implements CombatTarget {
  readonly id: number;
  readonly slot: number;
  readonly rig: CharacterRig;
  readonly object: THREE.Object3D;
  readonly position = new THREE.Vector2();
  readonly velocity = new THREE.Vector2();
  readonly radius = FOLLOWER_DEFAULTS.radius;
  readonly faction: Faction = 'vitale';
  readonly weapon: Weapon;

  health: number;

  readonly #animator: CharacterAnimator;
  readonly #pickupRadius: number;
  readonly #rescueRadius: number;
  readonly #followDistance: number;
  readonly #spacing: number;
  readonly #speed: number;
  readonly #coverSpeed: number;
  readonly #coverHoldTime: number;
  readonly #leashDistance: number = FOLLOWER_DEFAULTS.leashDistance;
  readonly #shootRange: number = FOLLOWER_DEFAULTS.shootRange;
  readonly #engageRange: number = FOLLOWER_DEFAULTS.engageRange;
  readonly #desired = new THREE.Vector2();
  readonly #step = new THREE.Vector2();
  readonly #target = new THREE.Vector2();
  readonly #coverTarget = new THREE.Vector2();
  readonly #aim = new THREE.Vector2();
  #state: FollowerState = 'waiting';
  #facing = 0;
  #coverLeft = 0;
  #downEmitted = false;
  #unsubscribeAlert: (() => void) | null = null;
  #unsubscribeDamage: (() => void) | null = null;

  constructor(config: FollowerConfig) {
    this.id = config.id;
    this.slot = config.slot;
    this.position.copy(config.spawn);
    this.#pickupRadius = config.pickupRadius ?? FOLLOWER_DEFAULTS.pickupRadius;
    this.#rescueRadius = config.rescueRadius ?? FOLLOWER_DEFAULTS.rescueRadius;
    this.#followDistance = config.followDistance ?? FOLLOWER_DEFAULTS.followDistance;
    this.#spacing = config.spacing ?? FOLLOWER_DEFAULTS.spacing;
    this.#speed = config.speed ?? FOLLOWER_DEFAULTS.speed;
    this.#coverSpeed = config.coverSpeed ?? FOLLOWER_DEFAULTS.coverSpeed;
    this.#coverHoldTime = config.coverHoldTime ?? FOLLOWER_DEFAULTS.coverHoldTime;
    this.health = config.maxHealth ?? FOLLOWER_DEFAULTS.maxHealth;
    this.weapon = new Weapon(config.weapon ?? FOLLOWER_PISTOL);
    this.rig = config.rig ?? buildCharacter(LOYAL_SOLDIER);
    this.object = this.rig.root;
    this.#animator = new CharacterAnimator(this.rig);
    this.#sync();

    this.#unsubscribeAlert = bus.on('enemy:alerted', ({ position }) => {
      this.#requestCover(new THREE.Vector2(position.x, position.z), null);
    });
    this.#unsubscribeDamage = bus.on('player:damaged', () => {
      this.#requestCover(null, null);
    });
  }

  get alive(): boolean {
    return this.#state !== 'down' && this.health > 0;
  }

  get active(): boolean {
    return this.#state === 'following' || this.#state === 'cover' || this.#state === 'shoot';
  }

  get rescued(): boolean {
    return this.#state === 'rescued';
  }

  get state(): FollowerState {
    return this.#state;
  }

  update(dt: number, context: FollowerUpdateContext): void {
    if (!this.alive) {
      this.#goDown();
      return;
    }

    this.weapon.update(dt);
    const enemy = this.#nearestVisibleEnemy(context);

    switch (this.#state) {
      case 'waiting':
        if (this.position.distanceTo(context.leader.position) <= this.#pickupRadius) {
          this.#state = 'following';
          this.#tryRescue(context);
        }
        this.velocity.set(0, 0);
        break;
      case 'following':
        if (enemy && this.#shouldShoot(context.leader, enemy)) {
          this.#state = 'shoot';
          this.velocity.set(0, 0);
          this.#shootStep(enemy, context);
          break;
        }
        this.#followStep(dt, context);
        this.#tryRescue(context);
        break;
      case 'cover':
        if (enemy && this.#coverLeft <= 0 && this.#shouldShoot(context.leader, enemy)) {
          this.#state = 'shoot';
          this.velocity.set(0, 0);
          this.#shootStep(enemy, context);
          break;
        }
        this.#coverStep(dt, context.world);
        this.#tryRescue(context);
        break;
      case 'shoot':
        if (!enemy || !this.#shouldShoot(context.leader, enemy)) {
          this.#state = 'following';
          this.#followStep(dt, context);
          this.#tryRescue(context);
          break;
        }
        this.#shootStep(enemy, context);
        this.#tryRescue(context);
        break;
      case 'rescued':
      case 'down':
        this.velocity.set(0, 0);
        break;
    }

    if (this.active) this.#resolveActorCollisions(context);
    this.#animator.update(dt, this.velocity.length(), this.#state === 'shoot');
    this.#sync();
  }

  damage(amount: number): void {
    if (!this.alive) return;
    this.health = Math.max(0, this.health - amount);
    if (this.health === 0) this.#goDown();
  }

  snapshot(): FollowerSnapshot {
    return {
      id: this.id,
      state: this.#state,
      health: this.health,
      position: this.position.clone(),
      slot: this.slot,
    };
  }

  dispose(): void {
    this.#unsubscribeAlert?.();
    this.#unsubscribeDamage?.();
    this.#unsubscribeAlert = null;
    this.#unsubscribeDamage = null;
    this.object.removeFromParent();
  }

  #followStep(dt: number, context: FollowerUpdateContext): void {
    this.#followTarget(context.leader.position, this.slot, this.#target);
    this.#separateFromFollowers(context.followers, this.#target);
    this.#addIdleOrbit(context.leader.position, this.#target);

    if (this.position.distanceTo(this.#target) <= ARRIVE_EPSILON) {
      this.velocity.set(0, 0);
      return;
    }
    this.#moveToward(dt, this.#target, this.#speed, context.world);
  }

  #coverStep(dt: number, world: CollisionWorld): void {
    this.#coverLeft -= dt;
    if (this.position.distanceTo(this.#coverTarget) > ARRIVE_EPSILON) {
      this.#moveToward(dt, this.#coverTarget, this.#coverSpeed, world);
      return;
    }

    this.velocity.set(0, 0);
    if (this.#coverLeft <= 0) this.#state = 'following';
  }

  #shootStep(enemy: CombatTarget, context: FollowerUpdateContext): void {
    this.#turnToward(1 / 30, enemy.position);
    this.velocity.set(0, 0);
    if (!context.combat) return;
    if (this.weapon.empty) this.weapon.reload();
    if (!this.weapon.pullTrigger()) return;
    this.#aim.copy(enemy.position);
    context.combat.fire(this.position, this.#aim, this.weapon.spec, this);
  }

  #tryRescue(context: FollowerUpdateContext): void {
    if (this.position.distanceTo(context.safehouse) > this.#rescueRadius) return;
    this.#state = 'rescued';
    this.velocity.set(0, 0);
    this.object.visible = false;
    const remaining = context.followers.filter((follower) => {
      if (follower.id === this.id) return false;
      return follower.alive && !follower.rescued;
    }).length;
    bus.emit('soldier:rescued', { id: this.id, remaining });
  }

  #requestCover(threat: THREE.Vector2 | null, world: CollisionWorld | null): void {
    if (!this.active || this.#state === 'shoot') return;
    const away = this.#target;
    if (threat) away.subVectors(this.position, threat);
    else away.set(-0.6, -1);
    if (away.lengthSq() === 0) away.set(0, -1);
    away.setLength(1.35);

    const side = this.slot % 2 === 0 ? 1 : -1;
    this.#coverTarget.set(this.position.x + away.x + side * 0.55, this.position.y + away.y);
    if (world && world.blocksLineOfSight(this.position, this.#coverTarget)) {
      this.#coverTarget.set(this.position.x - side * 0.65, this.position.y + away.y * 0.55);
    }
    this.#coverLeft = this.#coverHoldTime;
    this.#state = 'cover';
  }

  #followTarget(leader: THREE.Vector2, slot: number, out: THREE.Vector2): THREE.Vector2 {
    const row = Math.floor(slot / 2);
    const side = slot % 2 === 0 ? -1 : 1;
    const frontBack = row % 2 === 0 ? 1 : -0.35;
    return out.set(
      leader.x + side * this.#spacing * (row + 0.75),
      leader.y + this.#followDistance * frontBack + row * this.#spacing,
    );
  }

  #addIdleOrbit(leader: THREE.Vector2, target: THREE.Vector2): void {
    if (this.position.distanceTo(leader) > this.#leashDistance) return;
    const phase = this.id * 1.73;
    target.x += Math.sin(performanceSafeTime() * 0.8 + phase) * 0.18;
    target.y += Math.cos(performanceSafeTime() * 0.65 + phase) * 0.14;
  }

  #nearestVisibleEnemy(context: FollowerUpdateContext): Enemy | null {
    if (!context.enemies) return null;
    let best: Enemy | null = null;
    let bestDistance = this.#engageRange;
    for (const enemy of context.enemies) {
      if (!enemy.alive) continue;
      const distance = this.position.distanceTo(enemy.position);
      if (distance >= bestDistance) continue;
      if (context.world.blocksLineOfSight(this.position, enemy.position)) continue;
      best = enemy;
      bestDistance = distance;
    }
    return best;
  }

  #shouldShoot(leader: CombatTarget, enemy: CombatTarget): boolean {
    return (
      this.position.distanceTo(enemy.position) <= this.#shootRange &&
      this.position.distanceTo(leader.position) <= this.#leashDistance
    );
  }

  #resolveActorCollisions(context: FollowerUpdateContext): void {
    this.#pushAwayFrom(context.leader, context.world, 0.7);
    for (const other of context.followers) {
      if (other === this || !other.active) continue;
      if (this.id > other.id) continue;
      this.#pushAwayFrom(other, context.world, 0.5);
    }
  }

  #pushAwayFrom(actor: CombatTarget, world: CollisionWorld, strength: number): void {
    const minDistance = this.radius + actor.radius + ACTOR_GAP;
    const dx = this.position.x - actor.position.x;
    const dz = this.position.y - actor.position.y;
    const distanceSq = dx * dx + dz * dz;
    if (distanceSq >= minDistance * minDistance) return;

    const distance = Math.sqrt(distanceSq);
    if (distance < 0.0001) {
      const angle = this.id * 2.399963;
      this.#step.set(Math.cos(angle), Math.sin(angle));
    } else {
      this.#step.set(dx / distance, dz / distance);
    }

    const push = (minDistance - Math.max(distance, 0.0001)) * strength;
    this.#step.multiplyScalar(push);
    moveActor(this, this.#step, world, bodies);
  }

  #separateFromFollowers(followers: readonly Follower[], target: THREE.Vector2): void {
    for (const other of followers) {
      if (other === this || !other.active) continue;
      const dx = target.x - other.position.x;
      const dz = target.y - other.position.y;
      const distanceSq = dx * dx + dz * dz;
      const minDistance = this.radius + other.radius + 0.18;
      if (distanceSq >= minDistance * minDistance || distanceSq === 0) continue;
      const distance = Math.sqrt(distanceSq);
      const push = (minDistance - distance) / distance;
      target.x += dx * push * 0.5;
      target.y += dz * push * 0.5;
    }
  }

  #moveToward(dt: number, target: THREE.Vector2, speed: number, world: CollisionWorld): void {
    this.#turnToward(dt, target);
    this.#desired.subVectors(target, this.position);
    if (this.#desired.lengthSq() === 0) {
      this.velocity.set(0, 0);
      return;
    }
    this.#desired.setLength(speed);
    this.velocity.copy(this.#desired);
    this.#step.copy(this.velocity).multiplyScalar(dt);
    moveActor(this, this.#step, world, bodies);
  }

  #goDown(): void {
    if (this.#state === 'down') return;
    this.#state = 'down';
    this.health = 0;
    this.velocity.set(0, 0);
    this.object.visible = false;
    if (!this.#downEmitted) {
      this.#downEmitted = true;
      bus.emit('soldier:down', { id: this.id, remaining: 0 });
    }
  }

  #turnToward(dt: number, target: THREE.Vector2): void {
    const targetFacing = Math.atan2(target.x - this.position.x, target.y - this.position.y);
    const delta = wrapAngle(targetFacing - this.#facing);
    this.#facing += delta * Math.min(1, FOLLOWER_DEFAULTS.turnRate * dt);
  }

  #sync(): void {
    this.object.position.set(this.position.x, 0, this.position.y);
    this.object.rotation.y = this.#facing;
  }
}

function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

function performanceSafeTime(): number {
  return typeof performance === 'undefined' ? 0 : performance.now() / 1000;
}
